import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import {
  ReactNode, forwardRef, useEffect, useMemo, useState,
} from 'react';
import {
  Button,
  FormFeedback,
  FormGroup, Input, InputProps, Label,
} from 'reactstrap';

import LocaleService from '@services/LocaleService';
import { isEmail } from '@shared/helpers/validation';
import VisibilityOffIcon from '@shared/icons/VisibilityOffIcon';
import VisibilityOnIcon from '@shared/icons/VisibilityOnIcon';
import PasswordStrengthFeedback from '@shared/utils/PasswordStrengthFeedback/PasswordStrengthFeedback';

export interface InputFieldProps {
  type: InputProps['type']
  label?: string | ReactNode
  placeholder?: string
  name: string
  id: string
  required?: boolean
  fieldError?: string | any
  onInputChangeHandler?: (value: string) => void
  autoComplete?: InputProps['autoComplete']
  isPasswordStrength?: boolean
  showFeedback?: boolean
  selectOptions?: Array<string>
  defaultValue?: string
  customClassName?: string
  formGrpClassName?: string
  float?: boolean
  value?: string
  isReadOnly?: boolean
  isMobile?: boolean
  mobileCountryCode?: string
  maxLength?: number;
}

// eslint-disable-next-line react/display-name
const InputField = forwardRef(({
  type, label, placeholder, name, id, required = false, fieldError = '',
  onInputChangeHandler = () => {},
  autoComplete, defaultValue = '',
  isPasswordStrength = false,
  showFeedback = true, selectOptions = [],
  customClassName = '', float = true,
  value = '', formGrpClassName = '',
  isReadOnly = false, isMobile = false, mobileCountryCode = '',
  maxLength,
}: InputFieldProps, ref) => {
  const i18n = LocaleService.getTranslations('general');
  const [ isFocused, setIsFocused ] = useState(false);
  const [ error, setError ] = useState(fieldError);
  const [ isPasswordVisible, setPasswordVisibility ] = useState(false);

  useEffect(() => {
    if (!isEmpty(fieldError) && fieldError !== error) {
      setError(fieldError);
      return;
    }
    setError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ fieldError ]);

  useEffect(() => {
    // validation
    if (!isEmpty(value)) {
      switch (type) {
        case 'email': {
          if (!isEmail(value)) {
            setError(i18n.validation.invalid_email);
          } else {
            setError('');
          }
          break;
        }
        default:
          setError('');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ value ]);

  const handleOnFocus = () => setIsFocused(true);
  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onInputChangeHandler(e.target.value);
    setIsFocused(false);
  };

  const handleOnClickPasswordVisibility = () => setPasswordVisibility((prev) => !prev);

  const inputType = useMemo((): InputProps['type'] => {
    if (type === 'password' && isPasswordVisible) return 'text';
    if (type === 'password' && !isPasswordVisible) return 'password';
    return type;
  }, [ type, isPasswordVisible ]);

  const renderError = () => {
    if (typeof error === 'object' && !Array.isArray(error)) {
      return (
        <FormFeedback>
          {Object.keys(error).map((key: string, index: number) => (
            <div key={`${index.toString()}`}>
              {error[key][0]}
            </div>
          ))}
        </FormFeedback>
      );
    }

    return <FormFeedback>{error}</FormFeedback>;
  };

  return (
    <>
      <FormGroup floating={float} className={`form-group ${formGrpClassName}`}>
        <Input
          id={id}
          name={name}
          type={inputType}
          placeholder={placeholder}
          required={required}
          innerRef={ref as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          value={value}
          defaultValue={defaultValue}
          // onChange={(e) => setValue(e.target.value)}
          onChange={(e) => onInputChangeHandler(e.target.value)}
          invalid={!isEmpty(error)}
          autoComplete={autoComplete}
          maxLength={maxLength}
          className={classNames(customClassName, {
            'readonly-input': isReadOnly,
          })}
          disabled={isReadOnly}
        >
          {type === 'select' ? (
            <>
              {/** Default selected option - using placeholder */}
              <option disabled selected value="">{placeholder}</option>
              {selectOptions.length && selectOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </>
          ) : null}
        </Input>
        {type === 'password' && (
          <Button color="link" className="btn-visibility" onClick={handleOnClickPasswordVisibility}>
            {isPasswordVisible ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
          </Button>
        )}
        {((isMobile && isFocused) || (isMobile && value)) && (
          <span className="number-visibility">
            {`(${mobileCountryCode})`}
          </span>
        )}
        {label && (
          <Label for={id}>
            {required && <span className="required-symbol">* </span>}{label}
          </Label>
        )}
        {/* {showFeedback && <FormFeedback>{error}</FormFeedback>} */}
        {showFeedback && renderError()}

        {maxLength && type !== 'textarea' && (
          <div className="text-end">
            <small className="input-char-count">
              {value?.length || 0}/{maxLength}
            </small>
          </div>
        )}
      </FormGroup>
      {
        type === 'password' && isFocused
      && isPasswordStrength
      && <PasswordStrengthFeedback password={value} />
      }
    </>
  );
});

export default InputField;
